/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */

import Logger from '../../Logger/Logger';
import { Base, SubscriptionListener, Token } from './pubsubTypes';

const CustomPubSub: Base = (function (root, factory) {
  let PubSubInstance: Base = {} as Base;
  const loggerServiceInstance = Logger.getInstance();
  if (root) {
    if (root.PubSubInstance) {
      ({ PubSubInstance } = root);
      loggerServiceInstance.log('PubSubInstance already loaded, using existing version');
    } else {
      root.PubSubInstance = PubSubInstance;
      factory(PubSubInstance);
    }
  }

  return PubSubInstance;
})(
  (typeof window === 'object' && window) || (typeof globalThis === 'object' && globalThis) || this,
  function (PubSubInstance: Base) {
    let messages: Record<string, Record<Token, SubscriptionListener<any>>> = {};
    let lastUid = -1;
    const ALL_SUBSCRIBING_MSG = '*';

    function hasKeys(obj: Record<string, any>): boolean {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          return true;
        }
      }
      return false;
    }

    function throwException(ex: Error): () => void {
      return function reThrowException(): void {
        throw ex;
      };
    }

    function callSubscriberWithDelayedExceptions(
      subscriber: SubscriptionListener<any>,
      message: string,
      data: any,
    ): void {
      try {
        subscriber(message, data);
      } catch (ex) {
        setTimeout(throwException(ex as Error), 0);
      }
    }

    function callSubscriberWithImmediateExceptions(
      subscriber: SubscriptionListener<any>,
      message: string,
      data: any,
    ): void {
      subscriber(message, data);
    }

    function deliverMessage(
      originalMessage: string,
      matchedMessage: string,
      data: any,
      immediateExceptions: boolean,
    ): void {
      const subscribers = messages[matchedMessage];
      const callSubscriber = immediateExceptions
        ? callSubscriberWithImmediateExceptions
        : callSubscriberWithDelayedExceptions;

      if (!Object.prototype.hasOwnProperty.call(messages, matchedMessage)) {
        return;
      }

      for (const s in subscribers) {
        if (Object.prototype.hasOwnProperty.call(subscribers, s)) {
          callSubscriber(subscribers[s], originalMessage, data);
        }
      }
    }

    function createDeliveryFunction(message: string, data: any, immediateExceptions: boolean): () => void {
      return function deliverNamespaced(): void {
        let topic = String(message);
        let position: number = topic.lastIndexOf('.');

        deliverMessage(message, message, data, immediateExceptions);

        while (position !== -1) {
          topic = topic.substr(0, position);
          position = topic.lastIndexOf('.');
          deliverMessage(message, topic, data, immediateExceptions);
        }

        deliverMessage(message, ALL_SUBSCRIBING_MSG, data, immediateExceptions);
      };
    }

    function hasDirectSubscribersFor(message: string): boolean {
      const topic = String(message);
      const found = Boolean(Object.prototype.hasOwnProperty.call(messages, topic) && hasKeys(messages[topic]));
      return found;
    }

    function messageHasSubscribers(message: string): boolean {
      let topic = String(message);
      let found: boolean = hasDirectSubscribersFor(topic) || hasDirectSubscribersFor(ALL_SUBSCRIBING_MSG);
      let position: number = topic.lastIndexOf('.');

      while (!found && position !== -1) {
        topic = topic.substr(0, position);
        position = topic.lastIndexOf('.');
        found = hasDirectSubscribersFor(topic);
      }

      return found;
    }

    function publish(message: string, data: any, sync: boolean, immediateExceptions: boolean): boolean {
      message = typeof message === 'symbol' ? (message as any)?.toString?.() : message;

      const deliver = createDeliveryFunction(message, data, immediateExceptions);
      const hasSubscribers = messageHasSubscribers(message);

      if (!hasSubscribers) {
        return false;
      }

      if (sync === true) {
        deliver();
      } else {
        setTimeout(deliver, 0);
      }
      return true;
    }

    PubSubInstance.publish = function (message: string, data?: any): boolean {
      return publish(message, data, false, false);
    };

    PubSubInstance.publishSync = function (message: string, data?: any): boolean {
      return publish(message, data, true, true);
    };

    PubSubInstance.subscribe = function (message: string, func: SubscriptionListener<any>): Token {
      if (typeof func !== 'function') {
        return '';
      }

      message = typeof message === 'symbol' ? (message as any)?.toString?.() : message;

      if (!Object.prototype.hasOwnProperty.call(messages, message)) {
        messages[message] = {};
      }

      const token = `uid_${String(++lastUid)}`;
      messages[message][token] = func;

      return token;
    };

    // PubSubInstance.subscribeAll = function (func: SubscriptionListener<any>): Token {
    //   return PubSubInstance.subscribe(ALL_SUBSCRIBING_MSG, func);
    // };

    PubSubInstance.subscribeOnce = function (message: string, func: SubscriptionListener<any>): Base<any, string> {
      const token: Token = PubSubInstance.subscribe(message, () => {
        PubSubInstance.unsubscribe(token);
        // eslint-disable-next-line prefer-rest-params
        func.apply(this, arguments as unknown as [message: string, data?: any]);
      });
      return PubSubInstance;
    };

    PubSubInstance.clearAllSubscriptions = function (): void {
      messages = {};
    };

    const clearSubscriptions = function (topic: string): void {
      for (const m in messages) {
        if (Object.prototype.hasOwnProperty.call(messages, m) && m.indexOf(topic) === 0) {
          delete messages[m];
        }
      }
    };

    PubSubInstance.countSubscriptions = function (topic: string): number {
      let count = 0;
      for (const m in messages) {
        if (Object.prototype.hasOwnProperty.call(messages, m) && m.indexOf(topic) === 0) {
          // eslint-disable-next-line guard-for-in
          for (const _token in messages[m]) {
            count++;
          }
          break;
        }
      }
      return count;
    };

    PubSubInstance.getSubscriptions = function (topic: string): string[] {
      const list: string[] = [];
      for (const m in messages) {
        if (Object.prototype.hasOwnProperty.call(messages, m) && m.indexOf(topic) === 0) {
          list.push(m);
        }
      }
      return list;
    };

    PubSubInstance.unsubscribe = function (value: Token | SubscriptionListener<any>): Token | boolean {
      const descendantTopicExists = function (topic: string): boolean {
        for (const m in messages) {
          if (Object.prototype.hasOwnProperty.call(messages, m) && m.indexOf(topic) === 0) {
            return true;
          }
        }
        return false;
      };

      const isTopic: boolean =
        typeof value === 'string' &&
        (Object.prototype.hasOwnProperty.call(messages, value) || descendantTopicExists(value));

      const isToken: boolean = !isTopic && typeof value === 'string';
      const isFunction: boolean = typeof value === 'function';
      let result: Token | boolean = false;

      let m: string, message: Record<Token, SubscriptionListener<any>>, t: string;

      if (isTopic) {
        clearSubscriptions(value as string);
        return '';
      }

      for (m in messages) {
        if (Object.prototype.hasOwnProperty.call(messages, m)) {
          message = messages[m];

          if (isToken && message[value as string]) {
            delete message[value as string];
            result = value as string;
            break;
          }

          if (isFunction) {
            for (t in message) {
              if (Object.prototype.hasOwnProperty.call(message, t) && message[t] === value) {
                delete message[t];
                result = true;
              }
            }
          }
        }
      }

      return result;
    };

    return PubSubInstance;
  },
);

export default CustomPubSub;
